import React, { useRef, useEffect } from 'react';

import Modal from 'react-bootstrap/Modal';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { Toggle } from 'rsuite';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

import { AppInput, AppButton } from '../../components';

import useComponentState from '../../utils/useComponentState';
import useDebounce from '../../utils/useDebounce';
import { copyToClipboard } from '../../utils/helper';
import { registerAdministrator } from '../../api/auth';
import { getAllMerchant } from '../../api/merchant';
import { getSubMerchantsByMerchant } from '../../api/subMerchant';
import { searchClientDropdownList } from '../../api/Client';
import {
  MERCHANT_SCOPED_ROLES,
  CLIENT_SCOPED_ROLES,
  ROLE_MERCHANT_ADMINISTRATOR,
  ROLE_SUB_MERCHANT_ADMINISTRATOR,
  getAccessibleRoles,
  getRoleLabel,
} from '../../constants/roles';
import { createRole } from '../../constants/createRole';
import { getPortalAccess } from '../../constants/greetlyPortal';

import styles from './createUser.module.scss';

const MERCHANT_PORTAL_URL =
  process.env.REACT_APP_MERCHANT_PORTAL_URL || createRole.merchantPortalUrlFallback;

// "94" + 9-digit subscriber number, e.g. 94771234567.
const MAX_MOBILE_DIGITS = 11;

const CLIENT_SEARCH_DEBOUNCE_DELAY = 500;

// Strip everything except digits, keeping a single leading "+" if one was already present.
const sanitizeMobileInput = (value) => {
  const hasLeadingPlus = value.startsWith('+');
  const digitsOnly = value.replace(/[^0-9]/g, '');
  return hasLeadingPlus ? `+${digitsOnly}` : digitsOnly;
};

// Cap the digit count (excluding a leading "+") at MAX_MOBILE_DIGITS.
const capMobileDigits = (value) => {
  const hasLeadingPlus = value.startsWith('+');
  const digits = (hasLeadingPlus ? value.slice(1) : value).slice(
    0,
    MAX_MOBILE_DIGITS
  );
  return hasLeadingPlus ? `+${digits}` : digits;
};

// Auto-convert a local Sri Lankan number (e.g. 0771111111) to +94 format as the user types.
const formatMobileNumber = (value) => {
  const withCountryCode = value.startsWith('0') ? `+94${value.slice(1)}` : value;
  return capMobileDigits(withCountryCode);
};

const initialValues = {
  email: '',
  password: '',
  mobile: '',
  role: '',
  merchants: [],
  subMerchant: null,
  clientId: null,
  showGreetlyOnly: false,
  showGreetlyWithDetails: false,
};

// The Greetly toggles only make sense for a single Greetly-enabled merchant,
// so they stay hidden once more than one merchant is selected.
const isGreetlyMerchantSelected = (merchants) =>
  merchants.length === 1 && Boolean(merchants[0].isGreetlyEnable);

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(createRole.messages.emailInvalid)
    .required(createRole.messages.emailRequired)
    .trim(),
  password: Yup.string()
    .min(createRole.passwordMinLength, createRole.messages.passwordTooShort)
    .required(createRole.messages.passwordRequired),
  mobile: Yup.string()
    .matches(createRole.mobileRegex, createRole.messages.mobileInvalid)
    .required(createRole.messages.mobileRequired),
  role: Yup.string().required(createRole.messages.roleRequired),
  merchants: Yup.array().when('role', {
    is: (role) => MERCHANT_SCOPED_ROLES.includes(role),
    then: Yup.array().min(1, createRole.messages.merchantsRequired),
    otherwise: Yup.array(),
  }),
  subMerchant: Yup.mixed().when('role', {
    is: ROLE_SUB_MERCHANT_ADMINISTRATOR,
    then: Yup.mixed().required(createRole.messages.subMerchantRequired),
    otherwise: Yup.mixed().nullable(),
  }),
  clientId: Yup.mixed().when('role', {
    is: (role) => CLIENT_SCOPED_ROLES.includes(role),
    then: Yup.mixed().required(createRole.messages.clientRequired),
    otherwise: Yup.mixed().nullable(),
  }),
});

const CreateRoleModal = ({ show, onHide, token, currentUserRole, onCreated }) => {
  const formikRef = useRef(null);
  const latestClientReqIdRef = useRef(0);
  const [state, setState] = useComponentState({
    merchantOptions: [],
    merchantsLoading: false,
    merchantsError: null,
    subMerchantOptions: [],
    subMerchantsLoading: false,
    subMerchantsError: null,
    clientOptions: [],
    clientsLoading: false,
    clientsError: null,
    clientSearchTerm: '',
    submitting: false,
    submitError: null,
    createdCredentials: null,
  });

  const accessibleRoles = getAccessibleRoles(currentUserRole);
  const debouncedClientSearchTerm = useDebounce(
    state.clientSearchTerm,
    CLIENT_SEARCH_DEBOUNCE_DELAY
  );

  const handleCopy = (text) => {
    copyToClipboard(text);
    toast.success(createRole.messages.copiedToClipboard);
  };

  const renderGreetlyToggle = (field, label, values, setFieldValue) => (
    <div className={styles.greetly_toggle_row} key={field}>
      <Toggle
        checked={values[field]}
        disabled={state.submitting}
        onChange={(checked) =>
          handleGreetlyToggleChange(field, checked, setFieldValue)
        }
      />
      <span className={styles.greetly_toggle_label}>{label}</span>
    </div>
  );

  const renderCredentialRow = (label, value) => (
    <div className={styles.credential_row}>
      <span className={styles.credential_label}>{label}</span>
      <div className={styles.credential_value_group}>
        <span className={styles.credential_value}>{value}</span>
        <button
          type='button'
          className={styles.copy_btn}
          onClick={() => handleCopy(value)}
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      </div>
    </div>
  );

  const loadMerchants = async () => {
    if (state.merchantOptions.length > 0 || state.merchantsLoading) return;
    setState({ merchantsLoading: true, merchantsError: null });
    try {
      const response = await getAllMerchant(token);
      const options = response
        .slice()
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
        .map((merchant) => ({
          value: merchant.id,
          label: merchant.name,
          isGreetlyEnable: merchant.isGreetlyEnable,
        }));
      setState({ merchantOptions: options });
    } catch (err) {
      setState({ merchantsError: createRole.messages.merchantsLoadFailed });
    }
    setState({ merchantsLoading: false });
  };

  const loadSubMerchants = async (merchants) => {
    setState({ subMerchantsLoading: true, subMerchantsError: null });
    try {
      const results = await Promise.all(
        merchants.map((merchant) =>
          getSubMerchantsByMerchant(merchant.value, token)
        )
      );
      const merged = results.reduce(
        (acc, result) => acc.concat(result.subMerchants || []),
        []
      );
      const uniqueById = Array.from(
        new Map(
          merged.map((subMerchant) => [subMerchant.id, subMerchant])
        ).values()
      );
      const options = uniqueById
        .sort((a, b) =>
          a.location.toLowerCase() > b.location.toLowerCase() ? 1 : -1
        )
        .map((subMerchant) => ({
          value: subMerchant.id,
          label: subMerchant.location,
        }));
      setState({ subMerchantOptions: options });
    } catch (err) {
      setState({
        subMerchantsError: createRole.messages.subMerchantsLoadFailed,
      });
    }
    setState({ subMerchantsLoading: false });
  };

  const loadClients = async (searchTerm) => {
    const reqId = ++latestClientReqIdRef.current;
    setState({ clientsLoading: true, clientsError: null });
    try {
      const response = await searchClientDropdownList(
        token,
        searchTerm || '',
        'name',
        'clientName',
        'asc'
      );
      if (reqId !== latestClientReqIdRef.current) return;
      const clients = response?.clients || response || [];
      const options = clients.map((client) => ({
        value: client.id,
        label: client.name,
      }));
      setState({ clientOptions: options });
    } catch (err) {
      if (reqId !== latestClientReqIdRef.current) return;
      setState({ clientsError: createRole.messages.clientsLoadFailed });
    }
    if (reqId === latestClientReqIdRef.current) {
      setState({ clientsLoading: false });
    }
  };

  // Re-search whenever the user types in the client dropdown, but only while
  // a client-scoped role is actually selected (the dropdown is only mounted then).
  useEffect(() => {
    const role = formikRef.current && formikRef.current.values.role;
    if (CLIENT_SCOPED_ROLES.includes(role)) {
      loadClients(debouncedClientSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedClientSearchTerm]);

  const handleMobileChange = (event, setFieldValue, setFieldTouched) => {
    const sanitized = sanitizeMobileInput(event.target.value);
    setFieldValue('mobile', formatMobileNumber(sanitized));
    setFieldTouched('mobile', true, false);
  };

  const resetGreetlyToggles = (setFieldValue) => {
    setFieldValue('showGreetlyOnly', false);
    setFieldValue('showGreetlyWithDetails', false);
  };

  const handleGreetlyToggleChange = (field, checked, setFieldValue) => {
    setFieldValue(field, checked);
    if (checked) {
      setFieldValue(
        field === 'showGreetlyOnly'
          ? 'showGreetlyWithDetails'
          : 'showGreetlyOnly',
        false
      );
    }
  };

  const handleRoleChange = (role, setFieldValue) => {
    setFieldValue('role', role);
    setFieldValue('merchants', []);
    setFieldValue('subMerchant', null);
    setFieldValue('clientId', null);
    resetGreetlyToggles(setFieldValue);
    setState({ subMerchantOptions: [], clientSearchTerm: '' });
    if (MERCHANT_SCOPED_ROLES.includes(role)) {
      loadMerchants();
    }
    if (CLIENT_SCOPED_ROLES.includes(role)) {
      loadClients('');
    }
  };

  const handleClientChange = (selected, setFieldValue) => {
    setFieldValue('clientId', selected ? selected.value : null);
  };

  const handleClientInputChange = (inputValue, { action }) => {
    if (action === 'input-change') {
      setState({ clientSearchTerm: inputValue });
    }
  };

  const handleMerchantsChange = (selected, setFieldValue, isSubMerchantRole) => {
    const updated = isSubMerchantRole
      ? selected
        ? [selected]
        : []
      : selected || [];
    setFieldValue('merchants', updated);
    setFieldValue('subMerchant', null);
    if (!isGreetlyMerchantSelected(updated)) {
      resetGreetlyToggles(setFieldValue);
    }
    if (isSubMerchantRole) {
      if (updated.length > 0) {
        loadSubMerchants(updated);
      } else {
        setState({ subMerchantOptions: [] });
      }
    }
  };

  const resetLocalState = () => {
    setState({
      subMerchantOptions: [],
      submitError: null,
      createdCredentials: null,
    });
  };

  const handleClose = () => {
    formikRef.current && formikRef.current.resetForm();
    resetLocalState();
    onHide();
  };

  const handleSubmit = async (values, { resetForm }) => {
    setState({ submitting: true, submitError: null });
    try {
      const isMerchantScoped = MERCHANT_SCOPED_ROLES.includes(values.role);
      const isClientScoped = CLIENT_SCOPED_ROLES.includes(values.role);
      const body = {
        email: values.email.trim(),
        role: values.role,
        password: values.password,
        mobile: values.mobile.trim(),
        merchantIds: isMerchantScoped
          ? values.merchants.map((merchant) => String(merchant.value))
          : [],
        subMerchantId: values.subMerchant
          ? String(values.subMerchant.value)
          : null,
        portalAccess: getPortalAccess(values),
        clientId: isClientScoped && values.clientId ? Number(values.clientId) : null,
      };
      await registerAdministrator(body, token);
      setState({
        createdCredentials: { email: body.email, password: body.password },
      });
      resetForm();
      onCreated && onCreated();
    } catch (err) {
      setState({
        submitError:
          err?.response?.data?.message ||
          err?.message ||
          createRole.messages.createUserFailed,
      });
    }
    setState({ submitting: false });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size='lg'
      centered
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {state.createdCredentials
            ? createRole.titles.userCreated
            : createRole.titles.createRole}
        </Modal.Title>
      </Modal.Header>
      {state.createdCredentials ? (
        <>
          <Modal.Body>
            <div className={styles.credentials_header}>
              <p>{createRole.messages.shareCredentialsNote}</p>
              <button
                type='button'
                className={styles.copy_all_btn}
                onClick={() =>
                  handleCopy(
                    `${createRole.labels.url}: ${MERCHANT_PORTAL_URL}\n${createRole.labels.email}: ${state.createdCredentials.email}\n${createRole.labels.password}: ${state.createdCredentials.password}`
                  )
                }
              >
                <FontAwesomeIcon icon={faCopy} /> {createRole.buttonText.copyAll}
              </button>
            </div>
            <p className={styles.credentials_warning}>
              {createRole.messages.oneTimeViewWarning}
            </p>
            <div className={styles.credentials_box}>
              {renderCredentialRow(createRole.labels.url, MERCHANT_PORTAL_URL)}
              {renderCredentialRow(
                createRole.labels.email,
                state.createdCredentials.email
              )}
              {renderCredentialRow(
                createRole.labels.password,
                state.createdCredentials.password
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <AppButton
              text={createRole.buttonText.done}
              onClick={handleClose}
              containerStyle={{ width: '100%' }}
            />
          </Modal.Footer>
        </>
      ) : (
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, setFieldTouched }) => {
            const isMerchantScoped = MERCHANT_SCOPED_ROLES.includes(
              values.role
            );
            const isSubMerchantRole =
              values.role === ROLE_SUB_MERCHANT_ADMINISTRATOR;
            const isClientScoped = CLIENT_SCOPED_ROLES.includes(values.role);
            const showGreetlyToggles =
              values.role === ROLE_MERCHANT_ADMINISTRATOR &&
              isGreetlyMerchantSelected(values.merchants);
            return (
              <Form>
                <Modal.Body>
                  <AppInput
                    name='email'
                    label={createRole.labels.email}
                    type='email'
                    placeholder={createRole.placeholders.email}
                    loading={state.submitting}
                  />
                  <AppInput
                    name='password'
                    label={createRole.labels.password}
                    type='password'
                    password
                    placeholder={createRole.placeholders.password}
                    loading={state.submitting}
                  />
                  <AppInput
                    name='mobile'
                    label={createRole.labels.mobile}
                    type='text'
                    placeholder={createRole.placeholders.mobile}
                    loading={state.submitting}
                    value={values.mobile}
                    onChange={(e) =>
                      handleMobileChange(e, setFieldValue, setFieldTouched)
                    }
                  />

                  <div className={styles.form__group}>
                    <span className={styles.form__label}>
                      {createRole.labels.role}
                    </span>
                    <select
                      className='form-control'
                      value={values.role}
                      disabled={state.submitting}
                      onChange={(e) =>
                        handleRoleChange(e.target.value, setFieldValue)
                      }
                    >
                      <option value=''>{createRole.placeholders.selectRole}</option>
                      {accessibleRoles.map((role) => (
                        <option key={role} value={role}>
                          {getRoleLabel(role)}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage name='role'>
                      {(message) => (
                        <span className={styles.form__error}>{message}</span>
                      )}
                    </ErrorMessage>
                  </div>

                  {isMerchantScoped && (
                    <div className={styles.form__group}>
                      <span className={styles.form__label}>
                        {isSubMerchantRole
                          ? createRole.labels.merchant
                          : createRole.labels.merchants}
                      </span>
                      <Select
                        isMulti={!isSubMerchantRole}
                        isDisabled={state.submitting || state.merchantsLoading}
                        isLoading={state.merchantsLoading}
                        value={
                          isSubMerchantRole
                            ? values.merchants[0] || null
                            : values.merchants
                        }
                        options={state.merchantOptions}
                        onChange={(selected) =>
                          handleMerchantsChange(
                            selected,
                            setFieldValue,
                            isSubMerchantRole
                          )
                        }
                        placeholder={
                          isSubMerchantRole
                            ? createRole.placeholders.selectMerchant
                            : createRole.placeholders.selectMerchants
                        }
                      />
                      <ErrorMessage name='merchants'>
                        {(message) => (
                          <span className={styles.form__error}>{message}</span>
                        )}
                      </ErrorMessage>
                      {state.merchantsError && (
                        <span className={styles.form__error}>
                          {state.merchantsError}
                        </span>
                      )}
                    </div>
                  )}

                  {isSubMerchantRole && (
                    <div className={styles.form__group}>
                      <span className={styles.form__label}>
                        {createRole.labels.subMerchant}
                      </span>
                      <span className={styles.sub_merchant_note}>
                        {createRole.messages.subMerchantNote}
                      </span>
                      <Select
                        isClearable
                        isDisabled={
                          state.submitting || state.subMerchantsLoading
                        }
                        isLoading={state.subMerchantsLoading}
                        value={values.subMerchant}
                        options={state.subMerchantOptions}
                        onChange={(selected) =>
                          setFieldValue('subMerchant', selected)
                        }
                        placeholder={createRole.placeholders.selectSubMerchant}
                      />
                      <ErrorMessage name='subMerchant'>
                        {(message) => (
                          <span className={styles.form__error}>{message}</span>
                        )}
                      </ErrorMessage>
                      {state.subMerchantsError && (
                        <span className={styles.form__error}>
                          {state.subMerchantsError}
                        </span>
                      )}
                    </div>
                  )}

                  {isClientScoped && (
                    <div className={styles.form__group}>
                      <span className={styles.form__label}>
                        {createRole.labels.client}
                      </span>
                      <Select
                        isClearable
                        isDisabled={state.submitting || state.clientsLoading}
                        isLoading={state.clientsLoading}
                        value={
                          state.clientOptions.find(
                            (option) =>
                              String(option.value) === String(values.clientId)
                          ) || null
                        }
                        options={state.clientOptions}
                        onInputChange={handleClientInputChange}
                        onChange={(selected) =>
                          handleClientChange(selected, setFieldValue)
                        }
                        placeholder={createRole.placeholders.selectClient}
                      />
                      <ErrorMessage name='clientId'>
                        {(message) => (
                          <span className={styles.form__error}>{message}</span>
                        )}
                      </ErrorMessage>
                      {state.clientsError && (
                        <span className={styles.form__error}>
                          {state.clientsError}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Kept last so the toggles always sit at the bottom of the
                      modal, sub merchant select included. */}
                  {showGreetlyToggles && (
                    <div className={styles.form__group}>
                      {renderGreetlyToggle(
                        'showGreetlyOnly',
                        createRole.labels.showGreetlyOnly,
                        values,
                        setFieldValue
                      )}
                      {renderGreetlyToggle(
                        'showGreetlyWithDetails',
                        createRole.labels.showGreetlyWithDetails,
                        values,
                        setFieldValue
                      )}
                    </div>
                  )}

                  {state.submitError && (
                    <span className={styles.form__error}>
                      {getRoleLabel(state.submitError)}
                    </span>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <div className={styles.btn_row}>
                    <AppButton
                      text={createRole.buttonText.cancel}
                      onClick={handleClose}
                      containerStyle={{ width: '50%' }}
                    />
                    <AppButton
                      type='submit'
                      text={
                        state.submitting
                          ? createRole.buttonText.creating
                          : createRole.buttonText.create
                      }
                      loading={state.submitting}
                      containerStyle={{ width: '50%' }}
                    />
                  </div>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default CreateRoleModal;
